import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ProductFilters, { clearProductFilters, getProductFilterQuery } from "../components/storefront/ProductFilters";
import ProductListingShell, { ProductListingResults } from "../components/storefront/ProductListingShell";
import { listingHeroImage } from "../components/storefront/assets";
import { useI18n } from "../lib/i18n";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import Pagination, { PAGE_SIZE } from "../components/storefront/Pagination";
import { analytics } from "../lib/analytics";

const labelFromHandle = (handle = "") => handle
  .split("-")
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

export default function Shop() {
  const { locale, t } = useI18n();
  useDocumentTitle(t("shop.doc_title", "Mağaza"));
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const params = useMemo(() => ({
    q: sp.get("q") || undefined,
    category: sp.get("category") || undefined,
    sort: sp.get("sort") || "newest",
    ...getProductFilterQuery(sp),
    page: Math.max(1, Number(sp.get("page")) || 1),
    page_size: PAGE_SIZE,
  }), [sp]);

  const filterScope = useMemo(() => ({
    category: sp.get("category") || undefined,
    q: sp.get("q") || undefined,
  }), [sp]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    api.get("/products", { params })
      .then((r) => {
        if (!active) return;
        const items = r.data.items || [];
        setProducts(items);
        setTotal(Number(r.data.total ?? items.length));
      })
      .catch(() => {
        if (!active) return;
        setProducts([]);
        setTotal(0);
        setLoadError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params, retryKey]);

  const query = sp.get("q") || "";
  useEffect(() => { analytics.search(query); }, [query]);
  const activeCategory = sp.get("category") || "";
  const title = query
    ? `“${query}”`
    : activeCategory
      ? labelFromHandle(activeCategory)
      : t("shop.title", locale === "az" ? "Kataloq" : "Shop");
  const description = query
    ? t("shop.search_description", "Axtarış nəticələrini ölçü, rəng, material və mövcudluğa görə dəqiqləşdirin.")
    : t("shop.description", "Atelier Di Falco məhsullarını vahid kataloqda araşdırın; filterlər nəticəni səhifəni dəyişmədən yeniləyir.");

  return (
    <div className="bg-[var(--adf-cream)] text-[var(--adf-ink)]" data-testid="shop-page">
      <Header />
      <ProductListingShell
        title={title}
        eyebrow={query ? t("shop.search", "Axtarış") : t("shop.catalog", "Məhsul kataloqu")}
        description={description}
        image={listingHeroImage(query ? 1 : 0)}
        heroSlides={query ? [listingHeroImage(1)] : [listingHeroImage(0), listingHeroImage(2), listingHeroImage(6)]}
        total={total}
        loading={loading}
        toolbar={(
          <ProductFilters
            searchParams={sp}
            setSearchParams={setSp}
            total={total}
            loading={loading}
            scopeParams={filterScope}
          />
        )}
        testId="shop-listing"
      >
        <ProductListingResults
          listName="shop"
          products={products}
          loading={loading}
          loadError={loadError}
          onRetry={() => setRetryKey((value) => value + 1)}
          onClear={() => clearProductFilters(sp, setSp)}
          emptyTitle={query ? `“${query}” ${t("shop.no_results_for", "üzrə nəticə tapılmadı")}` : t("shop.empty_filters", "Uyğun məhsul tapılmadı")}
          emptyDescription={t("shop.empty_help", "Filterləri azaldın və ya concierge komandasından seçim üçün dəstək alın.")}
          emptyActions={(
            <>
              <Link to="/contact" className="adf-cta adf-cta-outline">{t("cta.concierge", "Concierge ilə əlaqə")}</Link>
            </>
          )}
          productTestId="shop-product"
        />

        <Pagination
          page={params.page}
          total={total}
          onChange={(next) => {
            const spNext = new URLSearchParams(sp);
            if (next <= 1) spNext.delete("page"); else spNext.set("page", String(next));
            setSp(spNext);
            // The grid is long; without this a page change looks like nothing
            // happened because the viewport is still halfway down the old one.
            document.getElementById("main")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          testId="shop-pagination"
        />
      </ProductListingShell>
      <Footer />
    </div>
  );
}
